import { ShiftLogAction, ShiftLogSubAction } from "../../../ShiftLogs/api/ShiftLogAction";

interface RenderActionParams {
  action?: string;
  subAction?: string;
}

function humanizeUnknownAction(action: string): string {
  return action
    .toLowerCase()
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

// eslint-disable-next-line complexity -- Large switch statement mapping actions to human-readable strings is intentionally comprehensive
export function renderAction(params: RenderActionParams): string {
  const { action, subAction } = params;

  if (!action) {
    return "---";
  }

  switch (action) {
    case ShiftLogAction.SHIFT_OPEN: {
      // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check -- Only relevant subActions are listed; default handles the rest
      switch (subAction) {
        case ShiftLogSubAction.SHIFT_INVITE_ACCEPTED: {
          return "Shift Added by Accepting Invite";
        }
        case ShiftLogSubAction.SHIFT_INVITE_DECLINED: {
          return "Shift Added by Declining Invite";
        }
        case ShiftLogSubAction.SHIFT_INVITE_EXPIRED: {
          return "Shift Added by Expiring Invite";
        }
        case ShiftLogSubAction.EXTERNAL_VENDOR_SHIFT_CREATE: {
          return "Shift Added by External Vendor";
        }
        default: {
          return "Shift Added";
        }
      }
    }
    case ShiftLogAction.SHIFT_DELETE:
    case ShiftLogAction.SHIFT_DELETE_FILLED: {
      return "Shift Deleted";
    }
    case ShiftLogAction.SHIFT_ASSIGN: {
      if (subAction === ShiftLogSubAction.SHIFT_INVITE_ACCEPTED) {
        return "Shift Assigned by Accepting Shift Invite";
      }
      if (subAction === ShiftLogSubAction.BOOKING_REQUEST_ACCEPTED) {
        return "Shift Assigned by Accepting Booking Request";
      }
      return "Shift Assigned";
    }
    case ShiftLogAction.SHIFT_REASSIGN: {
      return "Shift Reassigned";
    }
    case ShiftLogAction.SHIFT_UNASSIGN: {
      return "Worker Unassigned";
    }
    case ShiftLogAction.FACILITY_CANCEL:
    case ShiftLogAction.FACILITY_CANCEL_FILLED: {
      return "Workplace Cancel Recorded";
    }
    case ShiftLogAction.WORKER_CANCEL: {
      if (subAction === ShiftLogSubAction.SELF_WORKER_CANCEL) {
        return "Self Worker Cancel Recorded";
      }
      return "Worker Cancel Recorded";
    }
    case ShiftLogAction.NO_CALL_NO_SHOW: {
      if (subAction === ShiftLogSubAction.SELF_NO_CALL_NO_SHOW) {
        return "Self No Show Recorded";
      }
      return "No Show Recorded";
    }
    case ShiftLogAction.SHIFT_CLAIM: {
      return "Shift Claimed";
    }
    case ShiftLogAction.SHIFT_VERIFY: {
      return "Shift Verified";
    }
    case ShiftLogAction.SHIFT_TIME_CHANGE: {
      return "Shift Time Change";
    }
    case ShiftLogAction.SHIFT_NOTE_UPDATE: {
      return "Shift Note Update";
    }
    case ShiftLogAction.SHIFT_PAY_RATE_CHANGE: {
      return "Shift Pay Rate Changed";
    }
    case ShiftLogAction.SHIFT_CHARGE_RATE_CHANGE: {
      return "Shift Charge Rate Changed";
    }
    case ShiftLogAction.SHIFT_WAIT_AND_SAVE_CHARGE_UPDATED: {
      return "Wait & Save Charge Updated";
    }
    case ShiftLogAction.BILLABLE_ON: {
      return "Shift Billable Turned On";
    }
    case ShiftLogAction.BILLABLE_OFF: {
      return "Shift Billable Turned Off";
    }
    case ShiftLogAction.WORKER_LEFT_EARLY: {
      if (subAction === ShiftLogSubAction.WORKER_LEFT_EARLY_WITH_PERMISSION) {
        return "Left Early With Permission";
      }
      if (subAction === ShiftLogSubAction.WORKER_LEFT_EARLY_WITHOUT_PERMISSION) {
        return "Left Early Without Permission";
      }
      return "Worker Left Early";
    }
    case ShiftLogAction.REPLACEMENT_SHIFT_VERIFIED: {
      return "Replacement Shift Verified";
    }
    case ShiftLogAction.REPLACEMENT_SHIFT_POSTED: {
      return "Replacement Shift Posted";
    }
    case ShiftLogAction.POTENTIAL_WORKER_LEFT_EARLY: {
      return "Worker Potentially Left Early";
    }
    case ShiftLogAction.WORKPLACE_TIME_PROPOSAL_SENT: {
      return "Shift Time Proposal Sent";
    }
    case ShiftLogAction.WORKPLACE_TIME_PROPOSAL_ACCEPTED: {
      return "Shift Time Proposal Accepted";
    }
    case ShiftLogAction.WORKPLACE_TIME_PROPOSAL_DECLINED: {
      return "Shift Time Proposal Declined";
    }
    case ShiftLogAction.URGENCY_UPDATED: {
      return "Shift Urgency Updated";
    }
    case ShiftLogAction.MISSED_PUNCH_REQUESTED: {
      return "Missed Punch Requested by Worker";
    }
    case ShiftLogAction.MISSED_PUNCH_APPROVED: {
      return "Missed Punch Approved";
    }
    case ShiftLogAction.MISSED_PUNCH_DECLINED: {
      return "Missed Punch Declined";
    }
    case ShiftLogAction.WORKER_CLOCK_IN: {
      return "Worker Clocked In";
    }
    case ShiftLogAction.SHIFT_RESTORED: {
      return "Shift Restored";
    }
    case ShiftLogAction.MARKED_AS_NON_URGENT: {
      return "Marked as Non-Urgent";
    }
    case ShiftLogAction.TMZ_CHANGE:
    case ShiftLogAction.TIMEZONE_FIX: {
      return "Timezone Updated";
    }
    case ShiftLogAction.AUTO_SHIFT_PAY_RATE_CHANGE: {
      return "Auto Shift Pay Rate Changed";
    }
    case ShiftLogAction.AUTO_SHIFT_CHARGE_RATE_CHANGE: {
      return "Auto Shift Charge Rate Changed";
    }
    case ShiftLogAction.AUTO_SHIFT_BOOST_PAY_CHANGE: {
      return "Auto Shift Boost Pay Changed";
    }
    case ShiftLogAction.WORKER_FIRST_GEOFENCE_ENTRY: {
      return "Worker Entered Geofence";
    }
    case ShiftLogAction.FACILITY_CANCELLED_ME: {
      return "Facility Cancelled Me";
    }
    case ShiftLogAction.UNASSIGNED_WORKER_GEOFENCE_ENTRY: {
      return "Unassigned Worker Entered Geofence";
    }
    case ShiftLogAction.UNASSIGNED_WORKER_MOVED_TOWARDS_FACILITY: {
      return "Unassigned Worker Moved Towards Facility";
    }
    case ShiftLogAction.EXPECTED_ARRIVAL_TIME_ADDED: {
      return "Expected Arrival Time Added";
    }
    default: {
      return humanizeUnknownAction(action);
    }
  }
}
